import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { useKovaniePodlaKodu } from "@/api/hooks/useCennik";
import {
  hrubkaMaterialu,
  nesuladHrubkySlotu,
  predvolenaHrubkaDosky,
  useDostupneHrubky,
} from "@/api/hooks/useDostupneHrubky";
import { HrubkaDoskySelect } from "@/components/komponent/HrubkaDoskySelect";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { MEDZERA_CIEL } from "@/lib/korpus/types";
import {
  PODLOZKA_KATALOG,
  ZAVES_KATALOG,
  pocetZavesov,
} from "@/lib/korpus/generator";
import type { TypCela } from "@/lib/korpus/types";
import type { MaterialSlot, Zostava } from "@/api/schemas";

export interface DvereDialogProps {
  zakazkaId: string;
  /** Zostavy varianty — user si vyberie, do ktorej dvere vloží. */
  zostavy: Zostava[];
  /** Predvolená zostava (výber v editore). */
  selectedZostavaId?: string | null;
  sloty: MaterialSlot[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

/**
 * Samostatné dvere do existujúcej zostavy (napr. výmena dvierok) —
 * rozmery podľa KONSTRUKCIE.md §14 + závesy s reálnymi kódmi dielne
 * (56-3550/56-3690 + krížová podložka 56-0248 1:1).
 */
export function DvereDialog({
  zakazkaId,
  zostavy,
  selectedZostavaId,
  sloty,
  open,
  onOpenChange,
}: DvereDialogProps): JSX.Element {
  const [zostavaId, setZostavaId] = useState(
    selectedZostavaId ?? zostavy[0]?.id ?? "",
  );
  const [sirka, setSirka] = useState("600");
  const [vyska, setVyska] = useState("720");
  const [kridla, setKridla] = useState<"1" | "2">("1");
  const [typ, setTyp] = useState<TypCela>("nalozene");
  const hrubky = useDostupneHrubky();
  const hrubkaSlotu = (id: string): number | null =>
    hrubkaMaterialu(sloty.find((sl) => sl.id === id)?.material ?? null);
  const [slotId, setSlotId] = useState(sloty[0]?.id ?? "");
  // Hrúbka dverí podľa materiálu slotu (KOLO 168) — natvrdo 16/18/25 sa
  // dvere zo škárovky 19 mm nedali vložiť, server ich odmietol (422)
  const [t, setT] = useState(() =>
    String(predvolenaHrubkaDosky(hrubkaSlotu(sloty[0]?.id ?? ""), hrubky)),
  );
  const [zavesyRucne, setZavesyRucne] = useState(""); // "" = auto
  const [submitting, setSubmitting] = useState(false);
  const createDielec = useCreateDielec(zakazkaId, zostavaId);

  /** Zmena slotu nastaví hrúbku jeho materiálu — ako dielec (KOLO 159). */
  function zmenSlot(id: string): void {
    setSlotId(id);
    const z = hrubkaSlotu(id);
    if (z != null) setT(String(z));
  }

  // rozmery krídla — rovnaká logika ako builder (§14)
  const s = parseFloat(sirka) || 600;
  const v = parseFloat(vyska) || 720;
  const tMm = parseFloat(t) || 18;
  const slotZ = hrubkaSlotu(slotId);
  const hrubkaNesedi = nesuladHrubkySlotu(tMm, slotZ);
  const nKridel = parseInt(kridla, 10);
  // naložené/vložené = vstup − 2 mm/strana (vstup = čelná plocha resp.
  // otvor); polonaložené = otvor + prekrytie t/2−1 na stranu
  const pridaj = typ === "polonalozene" ? 2 * (tMm / 2 - 1) : -4;
  const celkovaSirka = s + pridaj;
  const sirkaKridla = Math.round(
    nKridel === 2 ? (celkovaSirka - MEDZERA_CIEL) / 2 : celkovaSirka,
  );
  const vyskaKridla = Math.round(v + pridaj);
  const zavesyNaKridlo =
    zavesyRucne !== ""
      ? Math.max(1, parseInt(zavesyRucne, 10) || 1)
      : pocetZavesov(sirkaKridla, vyskaKridla, tMm);
  const zavesySpolu = zavesyNaKridlo * nKridel;

  // kovanie podľa reálnych kódov dielne
  const zavesKod = ZAVES_KATALOG[typ];
  const zavesMat = useKovaniePodlaKodu(zavesKod, open);
  const podlozkaMat = useKovaniePodlaKodu(PODLOZKA_KATALOG, open);

  async function handleCreate(): Promise<void> {
    if (!zostavaId || hrubkaNesedi) return;
    setSubmitting(true);
    try {
      const suffix = Date.now().toString(36).slice(-4);
      for (let i = 1; i <= nKridel; i++) {
        const kod = nKridel === 1 ? `dvere_${suffix}` : `dvere_${suffix}_${i}`;
        await createDielec.mutateAsync({
          kod,
          nazov_strojovy: nKridel === 1 ? "dvere" : `dvere_${i}`,
          pocet_ks: 1,
          x_mm: String(sirkaKridla),
          y_mm: String(vyskaKridla),
          z_mm: String(tMm),
          material_slot_id: slotId || null,
          material_id_direct: null,
          nadmiera_x_mm: "5.00",
          nadmiera_y_mm: "5.00",
          // dvierka sú len porez — bez CNC nadmiery (KONSTRUKCIE.md §2)
          nadmiera_aktivna: false,
          smer_vlakien: "Y",
          hrana_x1_auto: true,
          hrana_x2_auto: true,
          hrana_y1_auto: true,
          hrana_y2_auto: true,
          poradie: 0,
        });
      }
      // závesy + krížové podložky 1:1 (materiál z cenníka podľa kódu)
      const kovanie = [
        {
          kod: `dvere_${suffix}_zaves`,
          nazov: zavesMat?.nazov ?? `Záves ${zavesKod}`,
          materialId: zavesMat?.id ?? null,
        },
        {
          kod: `dvere_${suffix}_podl`,
          nazov: podlozkaMat?.nazov ?? `Krížová podložka CLIP ${PODLOZKA_KATALOG}`,
          materialId: podlozkaMat?.id ?? null,
        },
      ];
      for (const k of kovanie) {
        await createDielec.mutateAsync({
          kod: k.kod,
          nazov_strojovy: k.nazov,
          pocet_ks: zavesySpolu,
          x_mm: null,
          y_mm: null,
          z_mm: null,
          material_slot_id: null,
          material_id_direct: k.materialId,
          nadmiera_x_mm: "0",
          nadmiera_y_mm: "0",
          nadmiera_aktivna: false,
          smer_vlakien: "0",
          hrana_x1_auto: false,
          hrana_x2_auto: false,
          hrana_y1_auto: false,
          hrana_y2_auto: false,
          poradie: 0,
        });
      }
      const nenajdene = [
        ...(zavesMat ? [] : [zavesKod]),
        ...(podlozkaMat ? [] : [PODLOZKA_KATALOG]),
      ];
      toast({
        title: `Dvere vytvorené (${nKridel} krídel + ${zavesySpolu}× záves a podložka)`,
        ...(nenajdene.length > 0
          ? {
              variant: "destructive" as const,
              description: `Kovanie sa nenašlo v cenníku: ${nenajdene.join(", ")} — dielec je bez materiálu.`,
            }
          : {}),
      });
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Chyba", description: extractErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vytvoriť dvere</DialogTitle>
          <DialogDescription className="sr-only">
            Dvere do vybraného otvoru vrátane závesov a podložiek.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Do zostavy</Label>
            <Select value={zostavaId} onValueChange={setZostavaId}>
              <SelectTrigger aria-label="Cieľová zostava"><SelectValue placeholder="Vyber zostavu…" /></SelectTrigger>
              <SelectContent>
                {zostavy.map((z) => (
                  <SelectItem key={z.id} value={z.id}>{z.kod} {z.nazov}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>{typ === "nalozene" ? "Šírka čelnej plochy" : "Šírka otvoru"}</Label><Input type="number" value={sirka} onChange={(e) => setSirka(e.target.value)} aria-label="Šírka dverí" /></div>
            <div><Label>{typ === "nalozene" ? "Výška čelnej plochy" : "Výška otvoru"}</Label><Input type="number" value={vyska} onChange={(e) => setVyska(e.target.value)} aria-label="Výška dverí" /></div>
            <HrubkaDoskySelect
              label="Hrúbka"
              ariaLabel="Hrúbka dverí"
              value={t}
              onChange={setT}
              matZ={slotZ}
              hrubky={hrubky}
            />
          </div>
          {hrubkaNesedi ? (
            <p className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {hrubkaNesedi}
            </p>
          ) : null}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Počet krídel</Label>
              <Select value={kridla} onValueChange={(v2) => setKridla(v2 as "1" | "2")}>
                <SelectTrigger aria-label="Počet krídel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 krídlo</SelectItem>
                  <SelectItem value="2">2 krídla</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Typ</Label>
              <Select value={typ} onValueChange={(v2) => setTyp(v2 as TypCela)}>
                <SelectTrigger aria-label="Typ dverí"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nalozene">Naložené (čelná plocha −2 mm/strana)</SelectItem>
                  <SelectItem value="vlozene">Vložené (otvor −2 mm/strana)</SelectItem>
                  <SelectItem value="polonalozene">Polonaložené (prekrytie t/2−1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>
                Závesy/krídlo{" "}
                <span className="text-muted-foreground">(auto {pocetZavesov(sirkaKridla, vyskaKridla, tMm)})</span>
              </Label>
              <Input
                value={zavesyRucne}
                onChange={(e) => setZavesyRucne(e.target.value)}
                placeholder="auto"
                inputMode="numeric"
                aria-label="Počet závesov na krídlo"
              />
            </div>
          </div>
          <div>
            <Label>Materiál</Label>
            <Select value={slotId} onValueChange={zmenSlot}>
              <SelectTrigger aria-label="Materiál"><SelectValue /></SelectTrigger>
              <SelectContent>
                {sloty.map((sl) => (
                  <SelectItem key={sl.id} value={sl.id}>{sl.slot_kod} {sl.label ?? ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="rounded-md border bg-background-alt/40 px-3 py-2 text-xs text-muted-foreground">
            Krídlo {sirkaKridla} × {vyskaKridla} × {t} mm ×{nKridel} +{" "}
            {zavesySpolu}× záves{" "}
            <b className={zavesMat ? "" : "text-destructive"}>{zavesKod}</b>
            {" "}+ {zavesySpolu}× podložka{" "}
            <b className={podlozkaMat ? "" : "text-destructive"}>{PODLOZKA_KATALOG}</b>
            {!zavesMat || !podlozkaMat ? " (nenájdené v cenníku)" : ""}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušiť</Button>
          <Button
            onClick={handleCreate}
            disabled={submitting || !zostavaId || hrubkaNesedi !== null}
            title={hrubkaNesedi ?? undefined}
          >
            {submitting ? "Vytvára sa…" : "Vytvoriť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
